/*
 * This helper encapculates Sentry methods.
 * Sentry is lazy-loaded only when a DSN is configured, so the SDK
 * is not bundled or fetched when Sentry is disabled.
 */
import { RuzukuGlobal, SessionUser } from 'server/types';
import { isRuzcalUrl } from 'shared/urls';

let sentryModule: typeof import('@sentry/browser') | undefined;
let isInited = false;

// Do not record request and response bodies for these paths
const PRIVATE_PATHS = ['auth', 'zapier-integration'];

function shouldRecordSession(configuration: RuzukuGlobal, isRuzcalSession: boolean) {
  const { tenant, user } = configuration;
  // Record all ruzcal sessions even with no signed-in user
  if (isRuzcalSession) {
    return true;
  }

  if (!user) {
    return false;
  }

  // Record Replay sessions for paying guides on core tenants
  if (tenant.isCore) {
    return !user.isRestricted && user.tier !== 'free';
  }

  // Record all guide/admin users on non-core tenants
  return user.level !== 'student';
}

export async function initSentry(configuration: RuzukuGlobal) {
  const { env, version, tenant } = configuration;
  if (!configuration.sentryDsn || env === 'development' || isInited) {
    return;
  }

  const Sentry = await import('@sentry/browser');
  sentryModule = Sentry;

  const isRuzcalSession = isRuzcalUrl(window.location.pathname);

  Sentry.init({
    dsn: configuration.sentryDsn,
    environment: env,
    release: version,
    replaysSessionSampleRate: shouldRecordSession(configuration, isRuzcalSession) ? 1 : 0.0,
    // Record all sessions that throws an error
    replaysOnErrorSampleRate: 1.0,
    integrations: [
      Sentry.replayIntegration({
        stickySession: true,
        maskAllText: false,
        blockAllMedia: false,
        maskAllInputs: false,
        // Mask texts with the following patterns
        mask: ['[data-private]'],
        // Ignore inputs with the following patterns
        ignore: ['[data-private]'],
        // We want to record all /rpx/* requests except `${PRIVATE_PATHS}`.
        // Sentry API is a bit weird for that case as they record the request
        // if it matches just one of the patterns in the array.
        // So we need to do it all in a single regex.
        networkDetailAllowUrls: [
          new RegExp(`^${window.location.origin}/rpx/(?!${PRIVATE_PATHS.join('|')}/)(.*)$`),
        ],
        // Extra headers to record
        networkResponseHeaders: ['cf-ray', 'cf-cache-status', 'rndr-id'],
      }),
    ],
  });
  const scope = Sentry.getCurrentScope();
  scope.setExtra('tenant', tenant.id);
  scope.setTag('coachmira', isRuzcalSession);
  isInited = true;
}

export function setSentryUser(user?: SessionUser) {
  if (!sentryModule) {
    return;
  }
  sentryModule.setUser(
    user
      ? {
          id: user.id,
          username: user.name,
          segment: user.level,
          timezone: user.timezone,
        }
      : null,
  );
}

export function setSentryExtra(key: string, value: string) {
  if (!sentryModule) {
    return;
  }
  sentryModule.getCurrentScope().setExtra(key, value);
}

export function captureException(err: Error) {
  if (!sentryModule) {
    return;
  }
  sentryModule.captureException(err);
}
